import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Observable, fromEvent, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../components/itemsPerPage.component';
import { Campaign } from '../../models/campaign.model';
import { ListItem, Specy } from '../../models/lists.model';
import { Organisation } from '../../models/organisation.model';
import { Page } from '../../models/page.model';
import { Parcel, ParcelStatus } from '../../models/parcel.model';
import { CampaignYearPipe } from '../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../pipes/toLabel.pipe';
import { CampaignService } from '../../services/campaign.service';
import { KeycloakService } from '../../services/keycloak.service';
import { ListType, ListsService } from '../../services/lists.service';
import { ModalService } from '../../services/modal.service';
import { ParcelFilter, ParcelService } from '../../services/parcel.service';
import { UserService } from '../../services/user.service';
import { Tooltip } from '../../utils/Tooltip';
import { TooltipContent } from '../../utils/TooltipContent';
import { TOOLTIP_TEXTS } from '../../utils/tooltip-texts';
import { toBoolean, toNumber } from '../../utils/utils';
import {
  ConfirmDeleteParcelDialogComponent,
  ConfirmDeleteParcelDialogContext,
} from './confirm-delete-dialog.component';
import { ParcelDetailsDialogComponent, ParcelDetailsDialogContext } from './parcel-details-dialog.component';
import { OrganisationService } from '../../services/organisation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'parcels-cmp',
  templateUrl: 'parcels.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    TranslatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [ParcelService, OrganisationService, UserService],
})
export class ParcelComponent implements OnInit, AfterViewInit {
  error: any;

  @ViewChild('termPrefixFilterParcel') termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterExploitation') termPrefixFilterExploitation: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termSpecyFilter') termSpecyFilter: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termOrganisationFilter') termOrganisationFilter: ElementRef;
  @ViewChild('termNoInvoiceFilter') termNoInvoiceFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;

  prefixFilterParcel: string = '';
  prefixFilterExploitation: string = '';
  prefixFilterIdParcel: number = null;
  specyFilter: number = null;

  campaignFilter: number | string = null;
  organisationFilter: number | string = null;
  noInvoiceFilter: boolean = null;
  public advisorFilter: string | number = null;

  parcels: Parcel[] = [];
  page: number = 1;
  maxSize: number = 5;
  itemsPerPage: number = 25;
  numPages: number = 1;
  length: number = 0;

  showRestricted: boolean;
  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;
  isAdmin: boolean;

  speciesDict: Map<number, Specy>;
  // TODO utiliser plutôt juste les espèces utilisées par des parcelles
  // de l'utilisateur
  species: Specy[];
  campaigns: Campaign[];

  showOrganisationFilter = false;
  organisations: Organisation[] = [];
  isNegoce: boolean;
  isManager: boolean;

  tooltipTexts = TOOLTIP_TEXTS;
  // static pour garder entre affichages de la liste
  static filterToApply: ParcelFilter;
  static allFiltersToShow: boolean;

  // Incomplete-only view support
  forceIncompleteView: boolean = false;
  includeNoOrderAsIncomplete: boolean = false;
  private azoteToolKey: string = 'azote';

  constructor(
    private parcelService: ParcelService,
    private listsService: ListsService,
    private campaignService: CampaignService,
    private organisationService: OrganisationService,
    private router: Router,
    private route: ActivatedRoute,
    private modal: ModalService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
  ) {
    this.showRestricted =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.showOrganisationFilter = this.isAdmin || this.isNegoce;
  }

  // ===== Data loading / paging ===================================================

  getParcelsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ParcelFilter) {
    this.parcelService
      .getParcelsAdvanced(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<Parcel>) {
    this.parcels = page.content;
    // TODO tools
    this.getParcelStatuses();
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addParcel() {
    this.router.navigate(['/parcels', 'new']);
  }

  deleteParcel(parcel: Parcel, event: any) {
    const context = new ConfirmDeleteParcelDialogContext();
    context.showClose = true;
    context.isBlocking = true;
    context.size = 'lg';
    context.parcelId = parcel.id;
    context.parent = this;
    this.modal.open(ConfirmDeleteParcelDialogComponent, context);
  }

  viewParcel(parcel: Parcel, event: any) {
    const context = new ParcelDetailsDialogContext();
    context.showClose = true;
    context.isBlocking = false;
    context.size = 'lg';
    context.parcelId = parcel.id;
    context.showEdit = true;
    this.modal.open(ParcelDetailsDialogComponent, context);
  }

  importParcel(parcel: Parcel) {
    const campaignId = this.campaigns && this.campaigns.length ? this.campaigns[0].id : null;
    if (campaignId) {
      this.router.navigate(['/parcels', 'new', campaignId, parcel.id]);
    }
  }

  getParcelStatuses() {
    if (!this.parcels || this.parcels.length === 0) return;

    const ids = this.parcels.map((x) => x.id);
    const index = new Map<number, Parcel>();
    this.parcels.forEach((p) => index.set(p.id, p));

    this.parcelService.getParcelStatuses(ids).subscribe((statuses) => {
      for (const s of statuses) {
        const p = index.get(s.parcelId);
        if (!p) {
          continue;
        }
        // uniquement utilisé dans cet écran
        // TODO l'ajouter dans le modèle ?
        (p as any)['canBeDeleted'] = this.canBeDeleted(s);
        (p as any)['canEditCrop'] = this.canEditCrop(s);
        (p as any).__status = s;
      }

      if (this.forceIncompleteView) {
        this.parcels = this.parcels.filter((p) => this.isIncompleteParcel((p as any).__status));
        this.length = this.parcels.length;
        this.numPages = Math.ceil(this.length / this.itemsPerPage) || 1;
      }
    });
  }

  canBeDeleted(status: ParcelStatus): boolean {
    if (!status) return false;
    if (status.countSuccessorParcels > 0) return false;
    if (status.toolStatuses) {
      for (const toolKey in status.toolStatuses) {
        const toolStatus = (status.toolStatuses as any)[toolKey];
        if (toolStatus && toolStatus.hasOrder) return false;
      }
    }
    return true;
  }

  private isIncompleteParcel(status: ParcelStatus): boolean {
    if (!status) return true;
    const toolStatuses: any = status.toolStatuses || {};
    const az = toolStatuses[this.azoteToolKey];
    if (!az) return this.includeNoOrderAsIncomplete;
    if (az.isComplete === true || az.completed === true) return false;
    return true;
  }

  private applyIncompleteFilter(shouldFilter: boolean) {
    this.forceIncompleteView = !!shouldFilter;
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      this.page = 1;
    }
  }

  canEditCrop(status: ParcelStatus): boolean {
    // Admin can edit everything including crop
    if (this.isAdmin) {
      return true;
    }

    // For other profiles, cannot edit crop if it has associated orders
    if (status && status.toolStatuses) {
      for (const toolKey in status.toolStatuses) {
        const toolStatuses: any = status.toolStatuses;
        const toolStatus = toolStatuses[toolKey];
        if (toolStatus.hasOrder) {
          return false;
        }
      }
    }
    return true;
  }

  ngOnInit() {
    let routeStatus: string = null;
    let routeOnlyIncomplete = false;
    let routeSpecyKey: string = null;
    let routeCampaignId: number = null;

    this.route.params.subscribe((params) => {
      routeStatus = params['status'];
      routeOnlyIncomplete = params['onlyIncomplete'] === '1' || params['onlyIncomplete'] === true;
      routeSpecyKey = params['specy'];
      if (params['campaignId']) routeCampaignId = +params['campaignId'];
    });

    // l'affichage des parcelles a besoin des espèces
    this.getSpecies().subscribe(() => {
      const hasRouteParams = routeStatus || routeOnlyIncomplete || routeSpecyKey || routeCampaignId;
      if (!ParcelComponent.filterToApply || hasRouteParams) {
        if (routeSpecyKey) this.specyFilter = this.resolveSpecyIdFromKey(routeSpecyKey);
        if (routeCampaignId) this.campaignFilter = routeCampaignId;

        const completionFromRoute: boolean = routeOnlyIncomplete || routeStatus === 'incomplete' ? false : null;

        const orderFromRoute: boolean = routeOnlyIncomplete
          ? true
          : routeStatus === 'ordered'
            ? true
            : routeStatus === 'notOrdered'
              ? false
              : null;

        this.forceIncompleteView = routeOnlyIncomplete || routeStatus === 'incomplete';

        const orgId = this.getOrganisationFilterId();
        const campaignId = this.getCampaignFilterId();

        ParcelComponent.filterToApply = {
          prefixParcel: this.prefixFilterParcel || null,
          prefixExploitation: this.prefixFilterExploitation || null,
          prefixIdParcel: toNumber(this.prefixFilterIdParcel),

          specyId: this.specyFilter || null,
          campaignId,
          organisationId: orgId,
          farmId: null,

          completionStatus: completionFromRoute,
          orderStatus: orderFromRoute,

          advisorId: this.advisorFilter && (this.advisorFilter + '').length >= 3 ? this.advisorFilter : null,

          passage: null,
          postalCodeExp: null,
          postalCodePar: null,
          dateAvant: null,
          dateApres: null,
          onlyIds: null,
          suspended: null,
          defaultWeekIdSet: null,
          datePlanAfter: null,
          datePlanBefore: null,
          noInvoice: toBoolean(this.noInvoiceFilter),
        };
      }
      this.updateUIWithFilters();
      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    const f = ParcelComponent.filterToApply;
    if (f) {
      if (this.isAdmin) {
        this.prefixFilterIdParcel = f.prefixIdParcel;
        this.noInvoiceFilter = toBoolean(f.noInvoice);
        if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
          this.termPrefixFilterIdParcel.nativeElement.value = this.prefixFilterIdParcel;
        }
        if (this.termNoInvoiceFilter && this.termNoInvoiceFilter.nativeElement) {
          this.termNoInvoiceFilter.nativeElement.value = this.noInvoiceFilter;
        }
      }
      if (this.isAdmin || this.isNegoce || this.isManager) {
        this.advisorFilter = f.advisorId || null;
        if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
          this.termAdvisorFilter.nativeElement.value = this.advisorFilter || '';
        }
      }

      this.prefixFilterParcel = f.prefixParcel;
      if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
        this.termPrefixFilterParcel.nativeElement.value = this.prefixFilterParcel;
      }
      this.prefixFilterExploitation = f.prefixExploitation;
      if (this.termPrefixFilterExploitation && this.termPrefixFilterExploitation.nativeElement) {
        this.termPrefixFilterExploitation.nativeElement.value = this.prefixFilterExploitation;
      }

      this.organisationFilter = toNumber(f.organisationId);
      if (this.showOrganisationFilter && this.termOrganisationFilter && this.termOrganisationFilter.nativeElement) {
        this.termOrganisationFilter.nativeElement.value = this.organisationFilter;
      }
      this.specyFilter = toNumber(f.specyId);
      if (this.termSpecyFilter && this.termSpecyFilter.nativeElement) {
        this.termSpecyFilter.nativeElement.value = this.specyFilter;
      }
      this.campaignFilter = toNumber(f.campaignId);
      if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
        this.termCampaignFilter.nativeElement.value = this.campaignFilter;
      }
    }

    const hasActiveHiddenFilters = this.organisationFilter !== null || this.noInvoiceFilter !== null;

    if (ParcelComponent.allFiltersToShow || hasActiveHiddenFilters) {
      if (!this.showAllFilters) {
        this.toggleAllFilters();
      }
    }
  }

  ngAfterViewInit() {
    // TODO améliorer => pas de debounce quand clearFilter a été appelé
    const observables: Observable<{}>[] = [];

    if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
      const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs1);
      obs1.subscribe(() => {
        this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value;
      });
    }

    if (this.termPrefixFilterExploitation && this.termPrefixFilterExploitation.nativeElement) {
      const obs2 = fromEvent(this.termPrefixFilterExploitation.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs2);
      obs2.subscribe(() => {
        this.prefixFilterExploitation = <string>this.termPrefixFilterExploitation.nativeElement.value;
      });
    }

    if (this.termSpecyFilter && this.termSpecyFilter.nativeElement) {
      const obs3 = fromEvent(this.termSpecyFilter.nativeElement, 'change');
      observables.push(obs3);
    }

    if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
      const obs4 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
      observables.push(obs4);
    }

    if (this.showOrganisationFilter && this.termOrganisationFilter && this.termOrganisationFilter.nativeElement) {
      const obs5 = fromEvent(this.termOrganisationFilter.nativeElement, 'change');
      observables.push(obs5);
    }

    if (this.isAdmin) {
      if (this.termNoInvoiceFilter && this.termNoInvoiceFilter.nativeElement) {
        const obs6 = fromEvent(this.termNoInvoiceFilter.nativeElement, 'change');
        observables.push(obs6);
      }
      if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
        const obs7 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
          debounceTime(300),
          distinctUntilChanged(),
        );
        observables.push(obs7);
        obs7.subscribe(() => {
          this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value;
        });
      }
      if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
        const obs8 = fromEvent(this.termAdvisorFilter.nativeElement, 'keyup').pipe(
          debounceTime(300),
          distinctUntilChanged(),
        );
        observables.push(obs8);
        obs8.subscribe(() => {
          this.advisorFilter = <string>this.termAdvisorFilter.nativeElement.value;
        });
      }
    }

    if (observables.length > 0) {
      merge(...observables)
        .pipe(debounceTime(10))
        .subscribe(() => {
          this.updateFilters();
        });
    }

    const self = this;
    this.getCampaigns().subscribe(function () {
      self.cdr.detectChanges();
    });
    this.getOrganisations().subscribe(function () {
      self.updateUIWithFilters();
      self.cdr.detectChanges();
    });
  }

  // ===== Reference data (species, campaigns, orgs) ==============================

  getSpecies(): Observable<Map<ListType, ListItem[]>> {
    // TODO handle error
    // share => plusieurs subscribers sans plusieurs requetes
    const obs = this.listsService.getListTypes(ListType, ListType.SPECIES).pipe(share());
    obs.subscribe((lists: Map<ListType, ListItem[]>) => {
      const species = lists.get(ListType.SPECIES) || [];
      this.species = species.sort((a, b) => a.label.localeCompare(b.label));
      this.speciesDict = new Map<number, Specy>();
      for (const specy of species) {
        this.speciesDict.set((specy as any).id, specy as any);
      }
    });
    return obs;
  }

  getCampaigns(): Observable<Campaign[]> {
    const obs = this.campaignService.getCampaigns().pipe(share());
    obs.subscribe((campaigns) => {
      this.campaigns = (campaigns || []).sort(Campaign.compare).reverse();
    });
    return obs;
  }

  getOrganisations(): Observable<Organisation[]> {
    if (this.isAdmin || this.isNegoce) {
      const obs = this.organisationService.getOrganisations().pipe(share());
      const self = this;
      obs.subscribe(function (orgs) {
        self.organisations = (orgs || []).sort(function (a, b) {
          return a.name.localeCompare(b.name);
        });
      });
      return obs;
    } else {
      return of([]);
    }
  }

  // ===== UI actions / filters ====================================================

  clearFilters() {
    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.prefixFilterIdParcel = null;
    this.specyFilter = null;
    this.campaignFilter = null;
    this.organisationFilter = null;
    this.noInvoiceFilter = null;
    this.advisorFilter = null;

    if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
      this.termPrefixFilterParcel.nativeElement.value = null;
    }
    if (this.termPrefixFilterExploitation && this.termPrefixFilterExploitation.nativeElement) {
      this.termPrefixFilterExploitation.nativeElement.value = null;
    }
    if (this.termSpecyFilter && this.termSpecyFilter.nativeElement) {
      this.termSpecyFilter.nativeElement.value = null;
    }
    if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
      this.termCampaignFilter.nativeElement.value = null;
    }
    if (this.noInvoiceFilter !== undefined && this.termNoInvoiceFilter && this.termNoInvoiceFilter.nativeElement) {
      this.termNoInvoiceFilter.nativeElement.value = null;
    }
    if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
      this.termAdvisorFilter.nativeElement.value = null;
    }
    if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
    }
    if (this.showOrganisationFilter && this.termOrganisationFilter && this.termOrganisationFilter.nativeElement) {
      this.termOrganisationFilter.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    const orgId = this.getOrganisationFilterId();
    const campaignId = this.getCampaignFilterId();
    const rawSpecy: any = this.specyFilter;
    const cleanSpecyId = rawSpecy === 'null' || rawSpecy === null ? null : rawSpecy;

    ParcelComponent.filterToApply = {
      prefixParcel: this.prefixFilterParcel || null,
      prefixExploitation: this.prefixFilterExploitation || null,
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),

      specyId: cleanSpecyId,
      campaignId,
      organisationId: orgId,
      farmId: null,

      completionStatus:
        ParcelComponent.filterToApply && typeof ParcelComponent.filterToApply.completionStatus === 'boolean'
          ? ParcelComponent.filterToApply.completionStatus
          : null,
      orderStatus: ParcelComponent.filterToApply ? ParcelComponent.filterToApply.orderStatus : null,

      advisorId: this.advisorFilter && (this.advisorFilter + '').length >= 3 ? this.advisorFilter : null,

      passage: ParcelComponent.filterToApply ? ParcelComponent.filterToApply.passage : null,
      postalCodeExp: null,
      postalCodePar: null,
      dateAvant: null,
      dateApres: null,
      onlyIds: null,
      suspended: null,
      defaultWeekIdSet: null,
      datePlanAfter: null,
      datePlanBefore: null,
      noInvoice: toBoolean(this.noInvoiceFilter),
    };

    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le binding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    this.getParcelsPaginated(pageNumber, null, null, ParcelComponent.filterToApply);
  }

  onChangeItemsPerPage(newValue: number) {
    // si itemsPerPage change et que page change ; par exemple page trop
    // grande pour le nombre d'éléments totaux => refresh de la pagination
    // mais si page reste identique, il faut le faire explicitement
    const page = this.page;
    this.cdr.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  getKmls() {
    const orgId = this.getOrganisationFilterId();
    const campaignId = this.getCampaignFilterId();

    const filter = {
      prefixParcel: this.prefixFilterParcel,
      prefixExploitation: this.prefixFilterExploitation,

      farmId: <any>null,
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      specyId: this.specyFilter,
      campaignId,
      organisationId: orgId,
      noInvoice: toBoolean(this.noInvoiceFilter),
      advisorId: this.advisorFilter,
    };
    this.parcelService.getKmls(null, null, filter);
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      if (this.allFilters && this.allFilters.nativeElement) {
        this.allFilters.nativeElement.style.display = 'none';
      }
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      if (this.allFilters && this.allFilters.nativeElement) {
        this.allFilters.nativeElement.style.display = 'block';
      }
    }
    ParcelComponent.allFiltersToShow = this.showAllFilters;
  }

  // ===== Helpers =================================================================

  private getOrganisationFilterId(): number {
    const raw = this.organisationFilter;
    if (raw === null || raw === undefined) {
      return null;
    }

    if (typeof raw === 'number') {
      const parsedNumber = toNumber(raw);
      return parsedNumber === null ? null : parsedNumber;
    }

    const rawString = String(raw);
    const colonIndex = rawString.indexOf(':');
    const withoutPrefix = colonIndex !== -1 ? rawString.substring(colonIndex + 1) : rawString;
    const trimmed = withoutPrefix.trim();

    if (!trimmed) {
      return null;
    }

    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return null;
    }

    const parsed = toNumber(trimmed);
    return parsed === null ? null : parsed;
  }

  private getCampaignFilterId(): number {
    const raw = this.campaignFilter;
    if (raw === null || raw === undefined) {
      return null;
    }

    if (typeof raw === 'number') {
      const parsedNumber = toNumber(raw);
      return parsedNumber === null ? null : parsedNumber;
    }

    const rawString = String(raw);
    const colonIndex = rawString.indexOf(':');
    const withoutPrefix = colonIndex !== -1 ? rawString.substring(colonIndex + 1) : rawString;
    const trimmed = withoutPrefix.trim();

    if (!trimmed) {
      return null;
    }

    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return null;
    }

    const parsed = toNumber(trimmed);
    return parsed === null ? null : parsed;
  }

  private resolveSpecyIdFromKey(key: string): number {
    if (!this.species) return null;
    const norm = (key || '').toLowerCase();

    const hit = this.species.find((s) => ((s.label || '') as any).toLowerCase() === norm);
    if (hit) return (hit as any).id;

    // fuzzy match ble/colza/orge
    const unaccent = (s: string) => (s || '').toLowerCase().replace('é', 'e');
    for (const s of this.species) {
      const lbl = unaccent(s.label as any);
      if (
        (norm === 'ble' && lbl.indexOf('ble') >= 0) ||
        (norm === 'colza' && lbl.indexOf('colza') >= 0) ||
        (norm === 'orge' && lbl.indexOf('orge') >= 0)
      ) {
        return (s as any).id;
      }
    }
    return null;
  }
}
